// Typo correction for email domains ("test@ggmail.com" -> "test@gmail.com"),
// derived from the Mailcheck algorithm (https://github.com/mailcheck/mailcheck):
// fuzzy-matches the typed domain against known providers with Sift3 distance.
// Domain and TLD lists extended with the modern set curated by
// https://github.com/smashsend/email-spell-checker (src/lib/config/index.ts).
const DOMAINS = [
  'gmail.com', 'googlemail.com', 'google.com', 'icloud.com', 'me.com', 'mac.com',
  'aol.com', 'aim.com', 'msn.com', 'comcast.net', 'sbcglobal.net', 'att.net',
  'verizon.net', 'bellsouth.net', 'charter.net', 'cox.net', 'earthlink.net',
  'optonline.net', 'qq.com', 'web.de', 'sky.com', 'btinternet.com', 'ymail.com',
  'rocketmail.com', 'telus.net', 'sympatico.ca', 'rogers.com', 'shaw.ca',
  'xtra.co.nz', 'optusnet.com.au', 'hey.com', 'proton.me', 'pm.me',
  'protonmail.com', 'zoho.com', 'yandex.com', 'titan.email',
  // The .com homes of the second-level providers below. Without them, a
  // ".con"-style typo resolves through the TLD list, where "co" (distance
  // 0.5) now beats "com" (distance 1) and produces "outlook.co".
  'yahoo.com', 'hotmail.com', 'outlook.com', 'live.com',
]

const SECOND_LEVEL_DOMAINS = ['yahoo', 'hotmail', 'mail', 'live', 'outlook', 'gmx']

const TOP_LEVEL_DOMAINS = [
  'com', 'com.au', 'com.tw', 'co', 'ca', 'co.nz', 'co.uk', 'de', 'fr', 'it',
  'ru', 'net', 'org', 'edu', 'gov', 'jp', 'nl', 'kr', 'se', 'eu', 'ie',
  'co.il', 'us', 'at', 'be', 'dk', 'hk', 'es', 'gr', 'ch', 'no', 'cz', 'in',
  'net.au', 'info', 'biz', 'mil', 'co.jp', 'sg', 'hu', 'uk', 'sk', 'ar', 'cf',
  'cl', 'cn', 'ga', 'gq', 'ir', 'ml', 'mx', 'nu', 'nz', 'ph', 'pl', 'ro',
  'tk', 'tw', 'ua', 'vg', 'ws', 'xn', 'za', 'app', 'au', 'ai', 'br', 'blog',
  'cloud', 'club', 'cc', 'dev', 'digital', 'fi', 'finance', 'id', 'io', 'me',
  'mobi', 'network', 'pw', 'so', 'xyz', 'software', 'to', 'tech', 'tv',
  'website',
]

const DOMAIN_THRESHOLD = 2
const SECOND_LEVEL_THRESHOLD = 2
const TOP_LEVEL_THRESHOLD = 2
const SIFT3_MAX_OFFSET = 5

function sift3Distance(a, b) {
  if (!a || a.length === 0) return !b || b.length === 0 ? 0 : b.length
  if (!b || b.length === 0) return a.length

  let cursor = 0
  let offsetA = 0
  let offsetB = 0
  let commonLength = 0

  while (cursor + offsetA < a.length && cursor + offsetB < b.length) {
    if (a.charAt(cursor + offsetA) === b.charAt(cursor + offsetB)) {
      commonLength += 1
    } else {
      offsetA = 0
      offsetB = 0
      for (let i = 0; i < SIFT3_MAX_OFFSET; i += 1) {
        if (cursor + i < a.length && a.charAt(cursor + i) === b.charAt(cursor)) {
          offsetA = i
          break
        }
        if (cursor + i < b.length && a.charAt(cursor) === b.charAt(cursor + i)) {
          offsetB = i
          break
        }
      }
    }
    cursor += 1
  }

  return (a.length + b.length) / 2 - commonLength
}

function splitEmail(email) {
  const parts = email.trim().split('@')
  if (parts.length < 2 || parts.some(part => part === '')) return null

  const domain = parts.pop()
  const domainParts = domain.split('.')
  if (domainParts.some(part => part === '')) return null

  let secondLevelDomain = ''
  let topLevelDomain = ''
  if (domainParts.length === 1) {
    [topLevelDomain] = domainParts
  } else {
    [secondLevelDomain] = domainParts
    topLevelDomain = domainParts.slice(1).join('.')
  }

  return {
    address: parts.join('@'),
    domain,
    secondLevelDomain,
    topLevelDomain,
  }
}

function findClosest(part, candidates, threshold) {
  if (!part) return null

  let closest = null
  let minDistance = threshold + 1

  for (let i = 0; i < candidates.length; i += 1) {
    if (part === candidates[i]) return part

    const distance = sift3Distance(part, candidates[i])
    if (distance < minDistance) {
      minDistance = distance
      closest = candidates[i]
    }
  }

  return closest
}

export function suggestEmail(email) {
  const parts = splitEmail(String(email || '').toLowerCase())
  if (!parts) return null

  // A known provider + known TLD combination (e.g. hotmail.fr) is not a typo.
  if (
    SECOND_LEVEL_DOMAINS.includes(parts.secondLevelDomain)
    && TOP_LEVEL_DOMAINS.includes(parts.topLevelDomain)
  ) return null

  const closestDomain = findClosest(parts.domain, DOMAINS, DOMAIN_THRESHOLD)
  if (closestDomain) {
    if (closestDomain === parts.domain) return null
    return { address: parts.address, domain: closestDomain, full: `${parts.address}@${closestDomain}` }
  }

  const closestSld = findClosest(parts.secondLevelDomain, SECOND_LEVEL_DOMAINS, SECOND_LEVEL_THRESHOLD)
  const closestTld = findClosest(parts.topLevelDomain, TOP_LEVEL_DOMAINS, TOP_LEVEL_THRESHOLD)

  let { domain } = parts
  let corrected = false
  if (closestSld && closestSld !== parts.secondLevelDomain) {
    domain = domain.replace(parts.secondLevelDomain, closestSld)
    corrected = true
  }
  if (closestTld && closestTld !== parts.topLevelDomain) {
    domain = domain.replace(new RegExp(`${parts.topLevelDomain}$`), closestTld)
    corrected = true
  }

  if (!corrected) return null
  return { address: parts.address, domain, full: `${parts.address}@${domain}` }
}
